/**
 * The captured tray, drawn with the app's own piece set
 * (`Components/Chessboard/pieces.ts` — the same SVGs chessground uses, so the
 * row and the board can never disagree about the art).
 *
 * The look is the old screen's (`PlayChess/components/CapturedPieces` L94-123):
 * one overlapping stack per piece type, each piece offset by a QUARTER of its
 * width, and a `+N` for the side that is ahead. Stacking is what makes 15
 * captures fit a 120 px tray.
 *
 * The row has a FIXED height: it sits in the 56 px PlayerRow, and the board
 * underneath must never move because a queen came off.
 *
 * `@/Components/Chessboard/pieces` is an ALLOWED exception to the View import
 * list of unit convention §1: the piece art cannot live anywhere else.
 */
import React from 'react';
import {Image, StyleSheet, View} from 'react-native';

import {PIECE_IMAGES} from '@/Components/Chessboard/pieces';
import type {Side} from '@/Store/liveGame/state';

import {SIZE, SPACE} from '../../theme/tokens';
import {Txt} from '../../ui';
import {CapturedModel} from './model';

/** 'P' (a missing WHITE pawn) -> 'wP'; 'p' -> 'bP'. */
const imageKey = (code: string): string =>
  code === code.toUpperCase() ? `w${code}` : `b${code.toUpperCase()}`;

const PIECE_OPACITY = 0.85;

/**
 * One captured piece. 20, a quarter up from `SIZE.capturedPiece`'s 16 — the
 * owner's clean-up item 1 of 2026-10-05: at 16 px inside a 56 px row the pieces
 * were decoration rather than information. It is a local constant and not a token
 * edit because `theme/tokens.ts` is the shared core (`src/Store/liveGame/SYNC.md`)
 * and the app lane's tray has its own geometry.
 */
export const PIECE_SIZE = 20;

/** how far each piece of a stack sits from the one under it (old L111) */
const OVERLAP = PIECE_SIZE / 4;

/** the width a stack of `count` pieces occupies (exported for the fit test) */
export const stackWidth = (count: number): number =>
  PIECE_SIZE + (count - 1) * OVERLAP;

export type CapturedViewProps = CapturedModel & {side: Side};

export default React.memo(function CapturedView({
  groups,
  lead,
  side,
}: CapturedViewProps) {
  if (groups.length === 0) {
    return null;
  }

  return (
    <View testID={`v2-captured-${side}`} style={styles.row}>
      <View style={styles.stacks}>
        {groups.map(group => (
          <View
            key={group.code}
            style={[styles.group, {width: stackWidth(group.count)}]}>
            {Array.from({length: group.count}, (unused, index) => (
              <Image
                // stable per (code, index): a stack only ever grows
                key={`${group.code}-${index}`}
                source={{uri: PIECE_IMAGES[imageKey(group.code)]}}
                style={[styles.piece, {left: index * OVERLAP}]}
                resizeMode="contain"
              />
            ))}
          </View>
        ))}
      </View>

      {lead > 0 ? (
        <Txt variant="caption" tone="muted" numeric>
          {`+${lead}`}
        </Txt>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  // the `+N` lives OUT here, outside the clip: it is the one thing in the tray
  // that must survive a full house of captures
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.xs,
    height: PIECE_SIZE,
  },
  // no gap between the groups: each stack's box already ends at its last piece,
  // so the quarter-width offsets are what separates them — and the 8p 2n 2b 2r
  // 1q worst case only fits the clip width without the five gaps
  stacks: {
    flexDirection: 'row',
    height: PIECE_SIZE,
    maxWidth: SIZE.capturedMax,
    overflow: 'hidden',
  },
  group: {
    height: PIECE_SIZE,
  },
  piece: {
    position: 'absolute',
    top: 0,
    width: PIECE_SIZE,
    height: PIECE_SIZE,
    opacity: PIECE_OPACITY,
  },
});
